import { BellRing } from 'lucide-react';
import { useEffect, useState } from 'react';
import { errorMessage, get, post } from '../lib/api';
import { toast } from './toasts';
import { Button, Spinner } from './ui';

interface PushConfig {
  publicKey: string;
  categories: Array<{ id: string; label: string }>;
}

const supported = () => typeof window !== 'undefined' && window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Chave pública VAPID (base64url) no formato que o navegador pede. */
function keyBytes(base64url: string): Uint8Array {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/**
 * Notificações com o app fechado (Web Push), por aparelho: ativar, escolher os avisos, testar e
 * desativar. A inscrição fica no servidor (push.subscriptions), ligada a quem está logado.
 */
export function PushSettings() {
  const [config, setConfig] = useState<PushConfig | null>(null);
  const [events, setEvents] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(supported() && Notification.permission === 'denied');

  useEffect(() => {
    if (!supported()) return;
    let alive = true;
    void (async () => {
      try {
        const cfg = await get<PushConfig>('/push/config');
        const sub = await currentSubscription();
        const mine = sub ? await get<{ subscribed: boolean; events: string[] }>(`/push/subscription?endpoint=${encodeURIComponent(sub.endpoint)}`) : null;
        if (!alive) return;
        setConfig(cfg);
        setEvents(mine?.subscribed ? mine.events : null);
      } catch {
        // sem push: a seção fica escondida
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!supported()) return null;
  if (!config) return <Spinner />;

  const save = async (sub: PushSubscription, next: string[]) => {
    const json = sub.toJSON();
    await post('/push/subscription', { endpoint: sub.endpoint, keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth }, events: next });
    setEvents(next);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setDenied(permission === 'denied');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const options = { userVisibleOnly: true, applicationServerKey: keyBytes(config.publicKey) as BufferSource };
      let sub: PushSubscription;
      try {
        sub = await reg.pushManager.subscribe(options);
      } catch {
        // Inscrição antiga com outra chave: troca.
        await (await reg.pushManager.getSubscription())?.unsubscribe();
        sub = await reg.pushManager.subscribe(options);
      }
      await save(sub, config.categories.map((c) => c.id));
      toast('Notificações ligadas neste aparelho', 'ok');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id: string, on: boolean) => {
    const sub = await currentSubscription();
    if (!sub || !events) return;
    try {
      await save(sub, on ? [...events, id] : events.filter((e) => e !== id));
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (sub) {
        await post('/push/subscription/remove', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setEvents(null);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    const sub = await currentSubscription();
    if (!sub) return;
    try {
      const r = await post<{ result: string }>('/push/subscription/test', { endpoint: sub.endpoint });
      toast(r.result === 'ok' ? 'Enviada: feche ou minimize o app para ver a notificação.' : 'O serviço do navegador recusou; ative de novo.', r.result === 'ok' ? 'ok' : 'error');
      if (r.result === 'gone') setEvents(null);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5 font-medium">
        <BellRing className="size-4" /> Com o app fechado (este aparelho)
      </div>
      {events === null ? (
        <>
          <p className="mb-2 text-xs text-fg-soft">Receba os avisos mesmo com a plataforma fechada, como as notificações do WhatsApp.</p>
          {denied ? (
            <p className="text-xs text-warn">As notificações deste site estão bloqueadas. Libere em Configurações do Android → Apps → Chrome (ou AIO Atendimento) → Notificações.</p>
          ) : (
            <Button size="sm" variant="primary" loading={busy} onClick={() => void enable()}>
              Ativar neste aparelho
            </Button>
          )}
        </>
      ) : (
        <>
          {config.categories.map((c) => (
            <label key={c.id} className="flex items-start gap-2 py-0.5">
              <input type="checkbox" className="mt-0.5" checked={events.includes(c.id)} onChange={(e) => void toggle(c.id, e.target.checked)} />
              <span className="text-xs">{c.label}</span>
            </label>
          ))}
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => void test()}>
              Testar
            </Button>
            <Button size="sm" variant="ghost" loading={busy} onClick={() => void disable()}>
              Desativar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
